import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useDeviceType from "../../../custom-hooks/DeviceType";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import {
  DownloadIcon,
  plusIcon,
  rightDirectionBlackIcon,
  searchIcon,
} from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import CodeDialog from "../dialogs/CodeDialog";
import { componentKey, setCloseAddCodeDialog } from "../SettingsSlice";
import {
  deleteCode,
  getCodeList,
  patchCodeData,
  postCodeData,
} from "../SettingsSaga";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";

const CodeScreen = () => {
  const { codeList, closeAddCodeDialog } = useSelector(
    (state: any) => state[componentKey],
  );

  const location = useLocation();
  const { code } = location.state || {};
  const { selectedCode } = useParams();

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchedValue, setSearchValue] = useState<string>("");
  const [status, setStatus] = useState("");
  const [defaultCodeDataRow, setDefaultCodeDataRow] = useState<any>({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedCodeId, setSelectedCodeId] = useState<string | null>(null);

  // INFINITE-SCROLL STATE
  const LIMIT = 25;
  const [pageNumber, setPageNumber] = useState(1);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef(false);

  // OPEN ADD/EDIT CODE DIALOG
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);

  const deviceType = useDeviceType();
  const codeType = selectedCode === "dsm-code" ? "DSM" : "ICD10";

  // CODE COLUMN DATA
  const CodeColumns = [
    {
      field: "codeName",
      label: <div>{code}</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.codeName}</p>;
      },
    },
    {
      field: "description",
      label: <div>Description</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.description}</p>
        );
      },
    },
    {
      field: "diagnosisName",
      label: <div>Diagnosis Name</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.diagnosisName || "-"}
          </p>
        );
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any, index: any) => {
        const status =
          row?.status === "Active"
            ? true
            : row?.status === "InActive"
              ? false
              : false;
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <EventEHRSwitchButton
              checked={status}
              onChange={() => {
                const apiPayload = {
                  payload: {
                    status: status ? "InActive" : "Active",
                  },
                  codeId: row?.id,
                };
                dispatch(patchCodeData(apiPayload));
              }}
            />{" "}
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "Edit Code",
                    onClickCb: () => {
                      setDefaultCodeDataRow(row);
                      setDialogState("edit"); // Open edit dialog
                    },
                  },
                  {
                    label: "Delete Code",
                    onClickCb: () => {
                      setSelectedCodeId(row?.id);
                      setOpenConfirmationDialog(true);
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  // CODE LIST ROW DATA
  const codeRowData = codeList?.data?.codes?.map((data: any) => ({
    id: data?.id || "-",
    codeName: data?.code || "-",
    description: data?.description || "-",
    diagnosisName: data?.diagnosisName || "-",
    status: data?.status || "-",
  }));
  // RESET TO PAGE 1 AND FETCH ON FILTER CHANGE
  useEffect(() => {
    setPageNumber(1);
    fetchingRef.current = false;
    getCode(1);
  }, [searchedValue, status]);

  // FETCH SUBSEQUENT PAGES TRIGGERED BY THE OBSERVER
  useEffect(() => {
    if (pageNumber > 1) getCode(pageNumber);
  }, [pageNumber]);

  // UNLOCK THE OBSERVER GATE ONCE NEW ROWS ARRIVE
  useEffect(() => {
    fetchingRef.current = false;
  }, [codeList?.data?.codes?.length]);

  // OBSERVE THE SENTINEL TO ADVANCE THE PAGE
  useEffect(() => {
    const node = loaderRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const totalPages = codeList?.data?.totalPages ?? 1;
        if (
          entries[0].isIntersecting &&
          !fetchingRef.current &&
          pageNumber < totalPages
        ) {
          fetchingRef.current = true;
          setPageNumber((p) => p + 1);
        }
      },
      { root: null, rootMargin: "100px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [pageNumber, codeList?.data?.totalPages]);

  useEffect(() => {
    if (closeAddCodeDialog) {
      dispatch(setCloseAddCodeDialog(false));
      setDialogState(null);
      setPageNumber(1);
      fetchingRef.current = false;
      getCode(1);
      setOpenConfirmationDialog(false); // for delete code confirmation dialog(for api failing scenario)
    }
  }, [closeAddCodeDialog]);

  const getCode = (page: number = pageNumber) => {
    const payload = {
      pageNumber: page,
      limit: LIMIT,
      // Backend `diagnosisName` ORs code + diagnosisName + description, so the
      // search box matches by code OR diagnosis text (e.g. "abdominal pain").
      diagnosisName: searchedValue?.trim(),
      status: status ? status : "",
      type: selectedCode === "dsm-code" ? "DSM" : "ICD10",
    };
    dispatch(getCodeList(payload));
  };

  const addCode = (data: any) => {
    console.log("data:", data);

    try {
      const payload: any = {
        type: selectedCode === "dsm-code" ? "DSM" : "ICD10",
        code: data?.codeName,
        description: data?.description,
        // status: "Active",
      };

      const isEdit = !!defaultCodeDataRow?.codeName;

      if (isEdit) {
        // On edit always send the key (empty string when cleared) so removing
        // the diagnosis name is persisted by the backend.
        payload.diagnosisName = data?.diagnosisName || "";
      } else if (data?.diagnosisName) {
        // On create only include it when provided.
        payload.diagnosisName = data?.diagnosisName;
      }

      if (isEdit) {
        const codeUpdatePayload = {
          payload: {
            ...payload,
          },
          codeId: defaultCodeDataRow?.id,
        };
        dispatch(patchCodeData(codeUpdatePayload));
      } else {
        dispatch(postCodeData(payload));
      }
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title={`${code}`} customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}>
              <SelectDropdown
                label=""
                name="status"
                placeholderValue="All Status"
                displayEmpty={true}
                value={status}
                options={[
                  { label: "Active", value: "Active" },
                  { label: "In Active", value: "InActive" },
                ]}
                onChangeCb={(e) => {
                  setStatus(e.target.value);
                }}
              />
            </div>
            <div>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e) => handleChange(e)}
                size="small"
                label="Search by code or description"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>

            {/* <Button
              startIcon={
                <img
                  src={DownloadIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Import CSV"
              variant="outlined"
              onClickCb={() => {}}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
                color: "#727272",
                border: "1px solid #D2D2D2",
                backgroundColor: "#FFFFFF",
                textTransform: "none",
                boxShadow: "none",
              }}
            ></Button> */}

            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label={`Add ${code}`}
              variant="contained"
              onClickCb={() => {
                setDefaultCodeDataRow({});
                setDialogState("add"); // Open add dialog
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table columns={CodeColumns} rows={codeRowData} />
          <div ref={loaderRef} style={{ height: 1 }} />
        </div>
      </div>
      {/* ADD CODE DIALOG */}
      {dialogState && (
        <CodeDialog
          defaultCodeData={defaultCodeDataRow}
          code={code}
          codeType={codeType}
          isEdit={dialogState === "edit"} // Determine edit mode
          isOpen={!!dialogState} // Dialog is open if dialogState is not null          isOpen={openCodeDialog}
          handleClose={() => {
            setDialogState(null); // Close the dialog by setting to null
          }}
          handleData={(data) => {
            addCode(data);
            console.log("data:", data);
          }}
        />
      )}

      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle={`Delete ${code}`}
          alertDescription={`Are you sure you want to delete this ${code}? Please confirm to proceed.`}
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
            } else if (action === "submit") {
              if (selectedCodeId) {
                const payload = {
                  codeId: selectedCodeId,
                  comesFrom: "",
                };
                dispatch(deleteCode(payload));
              }
              // setOpenConfirmationDialog(false);
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default CodeScreen;
